import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ShieldCheck, MapPin, Phone, Clock, Share2, MessageCircle, ShoppingCart, Banknote, ChevronLeft, Hash, Navigation, Send } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl, formatApiError, waLink, SAR } from "@/lib/api";
import { PartCard, Loader, Stars } from "@/components/shared";
import { useAuth } from "@/context/AuthContext";
import { directionsUrl } from "@/components/LocationBar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

export default function PartDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [delivery, setDelivery] = useState("pickup");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [ordering, setOrdering] = useState(false);
  const [open, setOpen] = useState(false);
  const [payMethod, setPayMethod] = useState("cod");

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get(`/parts/${id}`).then(({ data }) => setData(data)).catch(() => nav("/"));
    api.get(`/parts/${id}/similar`).then(({ data }) => setSimilar(data));
  }, [id, nav]);

  if (!data) return <Loader />;
  const { part, yard, category } = data;
  const images = part.images?.length ? part.images : [];

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: part.name, text: `${part.name} - ${part.price} ${SAR}`, url });
      else { await navigator.clipboard.writeText(url); toast.success("تم نسخ رابط القطعة"); }
    } catch {}
  };

  const shareWhatsApp = () => {
    const url = window.location.href;
    const text = `🚗 ${part.name}\n💰 السعر: ${part.price} ${SAR}\n🏪 التشليح: ${yard?.name || ""}\n🔗 ${url}\n\nعبر منصة مخزون كار`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const submitOrder = async () => {
    if (!user) { toast.error("سجّل الدخول أولًا لإرسال الطلب"); nav("/login"); return; }
    if (user.role !== "customer") { toast.error("الطلبات متاحة لحسابات العملاء فقط"); return; }
    if (delivery === "delivery" && !address.trim()) { toast.error("أدخل عنوان التوصيل"); return; }
    setOrdering(true);
    try {
      const { data: order } = await api.post("/orders", { part_id: part.id, quantity: qty, delivery_method: delivery, address, notes, payment_method: payMethod });
      if (payMethod === "online") {
        const { data } = await api.post("/payments/tap/charge", { order_id: order.id });
        setOpen(false);
        toast.info("جارٍ التحويل لبوابة الدفع...");
        window.location.assign(data.checkout_url);
        return;
      }
      toast.success("تم إرسال طلبك بنجاح! الدفع عند الاستلام.");
      setOpen(false);
      nav("/orders");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setOrdering(false); }
  };

  const contact = async () => {
    if (!user) { toast.error("سجّل الدخول للتواصل"); nav("/login"); return; }
    const text = window.prompt(`رسالتك إلى ${yard.name}:`, `مرحبًا، هل قطعة "${part.name}" ما زالت متوفرة؟`);
    if (!text) return;
    try { await api.post("/messages", { yard_id: yard.id, text }); toast.success("تم إرسال رسالتك للتشليح"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-6">
      <nav className="flex items-center gap-1 text-xs text-[var(--text-muted)] mb-5 flex-wrap">
        <Link to="/" className="hover:text-[var(--amber)]">الرئيسية</Link><ChevronLeft size={12} />
        <Link to="/search" className="hover:text-[var(--amber)]">قطع الغيار</Link><ChevronLeft size={12} />
        {category && <><Link to={`/search?category_id=${category.id}`} className="hover:text-[var(--amber)]">{category.name}</Link><ChevronLeft size={12} /></>}
        <span className="text-white truncate">{part.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* gallery */}
        <div>
          <div className="ts-card overflow-hidden aspect-[4/3] bg-[var(--bg-secondary)]">
            {images.length ? <img src={fileUrl(images[activeImg])} alt={part.name} className="h-full w-full object-cover" /> :
              <div className="flex h-full items-center justify-center text-[var(--text-muted)]">لا توجد صورة</div>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">
              {images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)}
                  className={`h-20 w-24 shrink-0 overflow-hidden rounded-xl border-2 ${i === activeImg ? "border-[var(--amber)]" : "border-transparent"}`}>
                  <img src={fileUrl(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* info */}
        <div>
          <div className="flex items-start justify-between gap-3">
            <h1 className="font-head text-2xl sm:text-3xl font-black text-white">{part.name}</h1>
            <button onClick={share} data-testid="share-part-button" className="p-2.5 rounded-xl border border-[var(--border-c)] text-[var(--text-muted)] hover:text-[var(--amber)] shrink-0"><Share2 size={18} /></button>
          </div>
          {part.part_number && <p className="mt-2 inline-flex items-center gap-1 text-sm text-[var(--text-muted)]"><Hash size={14} />رقم القطعة: {part.part_number}</p>}

          <div className="mt-4 flex items-baseline gap-2">
            <span className="font-head text-4xl font-black text-[var(--amber)]">{part.price.toLocaleString("ar")}</span>
            <span className="text-lg text-[var(--text-muted)]">{SAR}</span>
          </div>
          <div className="mt-3 inline-flex items-center gap-2 rounded-lg bg-[var(--emerald)]/15 px-3 py-1.5 text-sm font-bold text-[var(--emerald)]">
            <Banknote size={16} /> الدفع عند الاستلام متاح
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            <Spec label="السيارة" value={`${part.make} ${part.model}${part.trim ? " · " + part.trim : ""}`} />
            <Spec label="سنة الصنع" value={part.year_from ? `${part.year_from} - ${part.year_to}` : "—"} />
            <Spec label="القسم" value={part.category_name} />
            <Spec label="الحالة" value={part.condition} />
            <Spec label="الكمية المتوفرة" value={part.quantity} />
            <Spec label="المدينة" value={part.city} />
          </div>

          {part.description && <p className="mt-5 text-sm text-[var(--text-muted)] leading-relaxed">{part.description}</p>}

          {/* yard card */}
          {yard && (
            <Link to={`/yard/${yard.id}`} className="mt-6 ts-card p-4 flex items-center gap-3 block">
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-[var(--bg-secondary)]">
                {yard.logo ? <img src={fileUrl(yard.logo)} alt="" className="h-full w-full object-cover" /> :
                  <div className="flex h-full items-center justify-center font-head text-xl font-black text-[var(--amber)]">{yard.name?.[0]}</div>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5"><span className="font-bold text-white truncate">{yard.name}</span>{yard.verified && <ShieldCheck size={15} className="text-[var(--emerald)]" />}</div>
                <div className="flex items-center gap-2 text-xs text-[var(--text-muted)] mt-1"><Stars value={yard.rating_avg} size={12} /><span>({yard.rating_count})</span></div>
                <div className="flex items-center gap-3 text-xs text-[var(--text-muted)] mt-1">
                  <span className="flex items-center gap-1"><MapPin size={12} />{yard.address}</span>
                </div>
              </div>
            </Link>
          )}

          {/* actions */}
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <button data-testid="order-part-button" className="btn-amber flex-1 justify-center"><ShoppingCart size={18} /> اشترِ الآن</button>
              </DialogTrigger>
              <DialogContent className="bg-[var(--bg-card)] border-[var(--border-c)] text-white max-w-md" dir="rtl">
                <DialogHeader><DialogTitle className="text-white text-right">تأكيد الطلب</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div className="ts-card p-3 flex items-center gap-3">
                    {images[0] && <img src={fileUrl(images[0])} className="h-14 w-14 rounded-lg object-cover" alt="" />}
                    <div className="flex-1"><p className="text-sm font-bold text-white">{part.name}</p><p className="text-xs text-[var(--amber)]">{part.price.toLocaleString("ar")} {SAR}</p></div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">الكمية</label>
                    <input type="number" min={1} max={part.quantity} value={qty} data-testid="order-qty"
                      onChange={(e) => setQty(Math.max(1, Math.min(part.quantity, +e.target.value)))}
                      className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">طريقة الاستلام</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[["pickup", "استلام من التشليح"], ["delivery", "توصيل"]].map(([v, l]) => (
                        <button key={v} data-testid={`delivery-${v}`} onClick={() => setDelivery(v)}
                          className={`rounded-xl py-2.5 text-sm border ${delivery === v ? "border-[var(--amber)] text-[var(--amber)] bg-[var(--amber)]/10" : "border-[var(--border-c)] text-[var(--text-muted)]"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                  {delivery === "delivery" && (
                    <input placeholder="عنوان التوصيل" value={address} data-testid="order-address" onChange={(e) => setAddress(e.target.value)}
                      className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none" />
                  )}
                  <div>
                    <label className="text-xs font-bold text-[var(--text-muted)] block mb-1.5">طريقة الدفع</label>
                    <div className="grid grid-cols-1 gap-2">
                      {[["cod", "الدفع عند الاستلام", "ادفع مباشرة للتشليح عند الاستلام"], ["online", "الدفع الإلكتروني (مدى / Visa / Mastercard)", "دفع آمن عبر Tap Payments"]].map(([v, l, d]) => (
                        <button key={v} data-testid={`pay-method-${v}`} onClick={() => setPayMethod(v)} type="button"
                          className={`text-right rounded-xl px-3 py-2.5 border ${payMethod === v ? "border-[var(--amber)] bg-[var(--amber)]/10" : "border-[var(--border-c)]"}`}>
                          <span className={`block text-sm font-bold ${payMethod === v ? "text-[var(--amber)]" : "text-white"}`}>{l}</span>
                          <span className="block text-[11px] text-[var(--text-muted)]">{d}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                  <textarea placeholder="ملاحظات (اختياري)" value={notes} onChange={(e) => setNotes(e.target.value)}
                    className="w-full rounded-xl bg-[var(--bg-secondary)] px-3 py-2.5 outline-none resize-none" rows={2} />
                  <div className="flex items-center justify-between border-t pt-3" style={{ borderColor: "var(--border-c)" }}>
                    <span className="text-sm text-[var(--text-muted)]">الإجمالي</span>
                    <span className="font-head text-xl font-black text-[var(--amber)]">{(part.price * qty).toLocaleString("ar")} {SAR}</span>
                  </div>
                  <button data-testid="confirm-order-button" onClick={submitOrder} disabled={ordering} className="btn-amber w-full justify-center disabled:opacity-60">
                    {ordering ? "جارٍ المعالجة..." : (payMethod === "online" ? "المتابعة للدفع الإلكتروني" : "تأكيد الطلب")}
                  </button>
                </div>
              </DialogContent>
            </Dialog>
            <button data-testid="contact-seller-button" onClick={contact} className="flex-1 justify-center inline-flex items-center gap-2 rounded-xl border border-[var(--border-c)] py-3 font-bold text-white hover:border-[var(--amber)]">
              <MessageCircle size={18} /> تواصل مع البائع
            </button>
          </div>
          {yard && (yard.contact || yard.mobile) && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a data-testid="whatsapp-part-button" href={waLink(yard.contact || yard.mobile, `مرحبًا ${yard.name}، أنا مهتم بقطعة "${part.name}" (${part.price} ${SAR}) عبر مخزون كار. هل ما زالت متوفرة؟`)}
                target="_blank" rel="noopener noreferrer"
                className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 font-bold text-white hover:bg-[#1ebe57] transition-colors">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M17.5 14.4c-.3-.1-1.7-.9-2-1-.3-.1-.5-.1-.6.2-.2.3-.7.9-.8 1-.2.2-.3.2-.6.1-.3-.2-1.2-.5-2.3-1.4-.9-.8-1.4-1.7-1.6-2-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5-.1-.1-.6-1.5-.9-2-.2-.5-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.3-.3.3-1 1-1 2.4s1 2.8 1.2 3c.1.2 2 3.1 4.9 4.3.7.3 1.2.5 1.6.6.7.2 1.3.2 1.8.1.5-.1 1.7-.7 2-1.4.2-.7.2-1.2.2-1.4-.1-.1-.3-.2-.6-.3z"/><path d="M12 2C6.5 2 2 6.5 2 12c0 1.8.5 3.5 1.3 5L2 22l5.2-1.3c1.4.8 3 1.2 4.7 1.2 5.5 0 10-4.5 10-10S17.5 2 12 2zm0 18c-1.5 0-3-.4-4.2-1.2l-.3-.2-3.1.8.8-3-.2-.3C4.4 15 4 13.5 4 12c0-4.4 3.6-8 8-8s8 3.6 8 8-3.6 8-8 8z"/></svg>
                تواصل عبر واتساب
              </a>
              {yard.lat != null && yard.lng != null && (
                <a data-testid="part-directions-button" href={directionsUrl(yard.lat, yard.lng)} target="_blank" rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 rounded-xl border border-[var(--border-c)] py-3 font-bold text-white hover:border-[var(--amber)]">
                  <Navigation size={18} /> الاتجاهات للتشليح
                </a>
              )}
            </div>
          )}
          <button data-testid="share-whatsapp-button" onClick={shareWhatsApp}
            className="mt-3 w-full flex items-center justify-center gap-2 rounded-xl border border-[#25D366] py-3 font-bold text-[#25D366] hover:bg-[#25D366]/10 transition-colors">
            <Send size={18} /> شارك القطعة عبر واتساب
          </button>
        </div>
      </div>

      {similar.length > 0 && (
        <section className="mt-16">
          <h2 className="font-head text-xl font-black text-white mb-6">قطع مشابهة</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {similar.map((p) => <PartCard key={p.id} part={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}

const Spec = ({ label, value }) => (
  <div className="ts-card px-3 py-2.5">
    <p className="text-[11px] text-[var(--text-muted)]">{label}</p>
    <p className="text-sm font-bold text-white truncate">{value}</p>
  </div>
);
